
import { breakpoints, useMediaQuery } from 'src/shared/hooks/use-media-query';
import './style.module.scss';
import React,{ cloneElement, useEffect, useMemo } from 'react';
import { EventService } from 'src/event/service/event.service';
import { format24hto12h } from 'src/shared/utils/format';
import { cn } from 'src/shared/utils/props';


const calculateEventDimensions = (event, isMobile) => {
  const totalWidthPerHour = isMobile ? 180 : 250;
  const durationInMinutes =
    (new Date(event.endTime) - new Date(event.startTime)) / 60000;
  const width = (durationInMinutes / 60) * totalWidthPerHour;
  const leftOffset =
    (new Date(event.startTime).getMinutes() / 60) * totalWidthPerHour;
  return { leftOffset, width };
};

const calculateOffset = (dateTime, isMobile) => {
  const totalWidthPerHour = isMobile ? 180 : 250;
  return (new Date(dateTime).getMinutes() / 60) * totalWidthPerHour;
};

const TimeBorder = ({ hour, isMobile = false, time }) => {
  const isInCurrentHour = time && new Date(time).getHours() === hour;
  return isInCurrentHour ? (
    <div
      className={"dottedBorderEvent"}
      style={{ left: `${calculateOffset(time, isMobile)}px` }}
    />
  ) : null;
};

const Hour = ({
  eventCell,
  events,
  hour,
  isMobile = false,
  onEmptyHourClick = () => {},
}) => (
  <div
    className={"item"}
    id={`day-calendar-hour-${hour}`}
    key={hour}
    onClick={() => {
      if (events?.length === 0) {
        onEmptyHourClick(hour);
      }
    }}
  >
    {events?.map((event) => {
      const { eventType } = event;
      const { leftOffset, width } = calculateEventDimensions(event, isMobile);
      return (
        width >= 10 && (
          <div
            className={cn(
              "event",
              eventType && eventType !== 'allDayAvailability'
                ? "increaseZIndexOnHover"
                : '',
            )}
            key={event.id}
            style={{
              left: `${leftOffset}px`,
              width: `${width}px`,
              zIndex: event.zIndex,
            }}
          >
            {eventCell ? cloneElement(eventCell, { item: event }) : event.id}
          </div>
        )
      );
    })}
  </div>
);

const Day = ({
  eventCell,
  hours,
  isMobile = false,
  item,
  onEmptyHourClick,
  showBorders,
  timeBorders,
}) => (
  <>
    {hours.map((hour) => {
      const hourEvents = item?.events?.filter(
        (event) => new Date(event.startTime).getHours() === hour,
      );
      return (
        <div className={"dayHour"} key={`day-calendar-hour-${hour}`}>
          <Hour
            eventCell={eventCell}
            events={hourEvents}
            hour={hour}
            isMobile={isMobile}
            onEmptyHourClick={() => {
              if (hourEvents.length === 0) onEmptyHourClick({ hour, item });
            }}
          />
          {timeBorders.map((el) => {
            return (
              <>
                {el?.startTime && el?.endTime && (
                  <>
                    <TimeBorder
                      hour={hour}
                      isMobile={isMobile}
                      time={el.startTime}
                    />
                    <TimeBorder
                      hour={hour}
                      isMobile={isMobile}
                      time={el.endTime}
                    />
                  </>
                )}
              </>
            );
          })}
        </div>
      );
    })}
  </>
);

const DayCalendar = ({
  calendarId = null,
  containerClassName = '',
  emptyMessage = 'No data available',
  eventCell = null,
  eventDates = [],
  hideFirstColumn = false,
  initialFocusedTime = null,
  isLoading,
  itemCell = null,
  items = [],
  itemsTitle = null,
  onEmptyHourClick = () => {},
}) => {
  const isMobile = useMediaQuery(
    [breakpoints.mobile, breakpoints.tablet, breakpoints.desktop],
    [true, true, false],
  );
  const hours = [...Array.from({ length: 24 }).keys()];

  useEffect(() => {
    if (!initialFocusedTime && !eventDates) return;
    let calculatedFocusedTime;
    if (eventDates) {
      const temporaryEventDate = eventDates.sort(
        (a, b) => a.start - b.start,
      )[0];
      if (!temporaryEventDate) return;
      calculatedFocusedTime = EventService.eventDateStartTime(
        eventDates.sort((a, b) => a.start - b.start)[0],
      );
    }
    const minutes = new Date(
      initialFocusedTime ?? calculatedFocusedTime,
    ).getMinutes();
    const hours = new Date(
      initialFocusedTime ?? calculatedFocusedTime,
    ).getHours();
    const offset = hours + minutes / 60;
    const hourWidth = isMobile ? 180 : 250;
    const focusedHourOffset = offset * hourWidth;
    const viewportWidth = window.innerWidth;
    const hoursVisible = viewportWidth / hourWidth;
    const centerOffset = (hoursVisible / 5) * hourWidth;
    const scrollPosition = isMobile
      ? focusedHourOffset
      : focusedHourOffset - centerOffset;
    const calendarElement = document.querySelector(
      `#day-calendar-${calendarId ?? ''}`,
    );
    if (calendarElement) {
      calendarElement.scrollLeft = scrollPosition;
    }
  }, [initialFocusedTime, calendarId, eventDates]);

  const timeBorders = useMemo(() => {
    return eventDates.map((eventDate) => {
      const eventDateStartDateTime = EventService.eventDateStartTime(eventDate);
      const eventDateEndDateTime = EventService.eventDateEndTime(eventDate);
      return {
        endTime: eventDateEndDateTime,
        startTime: eventDateStartDateTime,
      };
    });
  }, [eventDates]);


  return (
    <div
      className={cn("dayCalendar", containerClassName)}
      id={`day-calendar-${calendarId ?? ''}`}
    >
      <div
        className={cn("row", "stickyTop")}
        style={{
          gridTemplateColumns: hideFirstColumn
            ? `repeat(${hours.length}, 1fr)`
            : isMobile
              ? `80px repeat(${hours.length}, 1fr)`
              : `200px repeat(${hours.length}, 1fr)`,
        }}
      >
        {!hideFirstColumn && (
          <div className={cn("item", "leftItem", "topItem")}>
            {itemsTitle}
          </div>
        )}
        {hours.map((hour) => (
          <div className={"dayHour"} key={hour}>
            <div
              className={cn("item", "topItem")}
              id={`day-calendar-hour-${hour}`}
              style={{ height: '50px' }}
            >
              {format24hto12h(`${hour}:00`, true)}
            </div>
            {timeBorders.map((el) => {
              return (
                <>
                  <TimeBorder
                    hour={hour}
                    isMobile={isMobile}
                    time={el.startTime}
                  />
                  <TimeBorder
                    hour={hour}
                    isMobile={isMobile}
                    time={el.endTime}
                  />
                </>
              );
            })}
          </div>
        ))}
      </div>
      {items.map((item) => (
        <div
          className={"row"}
          key={item.id}
          style={{
            gridTemplateColumns: hideFirstColumn
              ? `repeat(${hours.length}, 1fr)`
              : isMobile
                ? `80px repeat(${hours.length}, 1fr)`
                : `200px repeat(${hours.length}, 1fr)`,
          }}
        >
          {!hideFirstColumn && (
            <div className={cn(item, "leftItem")}>
              {itemCell
                ? cloneElement(itemCell, { item })
                : item.coach?.fullName || `Coach ${item.id}`}
            </div>
          )}
          <Day
            eventCell={eventCell}
            hours={hours}
            isMobile={isMobile}
            item={item}
            onEmptyHourClick={onEmptyHourClick}
            timeBorders={timeBorders}
          />
        </div>
      ))}
      {items.length === 0 && !isLoading && (
        <div className={"noData"}>{emptyMessage}</div>
      )}
    </div>
  );
};

export default DayCalendar;
